import React, { useEffect, useState } from 'react';
import { plannedSchedule } from './plannedSegments';
import { rrhhCorePost } from './rrhhCoreApi';
import { formatDisplayDate } from './rrhhUtils';

function blankSegment(date) {
  return {
    entrada: { id: null, fecha: date, hora: '', actividad: '', ubicacion: '' },
    salida: { id: null, fecha: date, hora: '', actividad: '', ubicacion: '' },
  };
}

function nextIsoDate(value) {
  const raw = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const date = new Date(`${raw}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

function markBelongsToJournal(mark, journalDate) {
  const date = String(mark?.fecha || '').slice(0, 10);
  const journal = String(journalDate || '').slice(0, 10);
  if (!date || !journal) return false;
  if (String(mark?.tipo || '').trim().toUpperCase() === 'SALIDA') {
    return date === journal || date === nextIsoDate(journal);
  }
  return date === journal;
}

function originalMarkIds(row) {
  return [...new Set((Array.isArray(row?.marcas_detalle) ? row.marcas_detalle : [])
    .filter((mark) => markBelongsToJournal(mark, row?.fecha))
    .map((mark) => Number(mark.id))
    .filter(Number.isInteger))];
}

function editorSegments(row) {
  const segments = Array.isArray(row?.tramos) ? row.tramos : [];
  if (!segments.length) return [blankSegment(row.fecha)];
  return segments.map((segment) => ({
    entrada: {
      id: segment.entrada_id || null,
      fecha: segment.entrada_fecha || row.fecha,
      hora: segment.entrada_hora || '',
      actividad: segment.entrada_actividad || '',
      ubicacion: segment.entrada_ubicacion || '',
    },
    salida: {
      id: segment.salida_id || null,
      fecha: segment.salida_fecha || row.fecha,
      hora: segment.salida_hora || '',
      actividad: segment.salida_actividad || '',
      ubicacion: segment.salida_ubicacion || '',
    },
  }));
}

export function timeInputValue(value) {
  const raw = String(value || '').replace(/[^\d:]/g, '').slice(0, 5);
  if (raw.includes(':')) {
    const [hours = '', minutes = ''] = raw.split(':');
    return `${hours.slice(0, 2)}:${minutes.slice(0, 2)}`;
  }
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  if (digits.length === 3 && Number(digits.slice(0, 2)) > 23) {
    return `0${digits.slice(0, 1)}:${digits.slice(1)}`;
  }
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

export function normalizeTime24(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  let hours;
  let minutes;
  if (/^\d{1,2}$/.test(raw)) {
    hours = Number(raw);
    minutes = 0;
  } else if (/^\d{3,4}$/.test(raw)) {
    const digits = raw.padStart(4, '0');
    hours = Number(digits.slice(0, 2));
    minutes = Number(digits.slice(2));
  } else {
    const match = /^(\d{1,2}):(\d{1,2})$/.exec(raw);
    if (!match) return null;
    hours = Number(match[1]);
    minutes = Number(match[2]);
  }
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function MarkFields({ label, value, onChange }) {
  function update(field, nextValue) { onChange({ ...value, [field]: nextValue }); }
  function finishTime() {
    const normalized = normalizeTime24(value.hora);
    if (normalized !== null) update('hora', normalized);
  }
  return <div className="rrhh-journal-mark-fields">
    <strong>{label}</strong>
    <input className="form-input" type="date" value={value.fecha} onChange={(event) => update('fecha', event.target.value)} aria-label={`Fecha ${label.toLowerCase()}`} />
    <input
      className="form-input rrhh-journal-time"
      type="text"
      inputMode="numeric"
      autoComplete="off"
      maxLength={5}
      placeholder="HH:MM"
      pattern="(?:[01][0-9]|2[0-3]):[0-5][0-9]"
      title="Hora en formato 24 horas (HH:MM)"
      value={value.hora}
      onChange={(event) => update('hora', timeInputValue(event.target.value))}
      onBlur={finishTime}
      aria-label={`Hora ${label.toLowerCase()} en formato 24 horas`}
    />
    <input className="form-input" value={value.ubicacion} onChange={(event) => update('ubicacion', event.target.value)} placeholder="Ubicación detectada" aria-label={`Ubicación ${label.toLowerCase()}`} />
    <input className="form-input" value={value.actividad} onChange={(event) => update('actividad', event.target.value)} placeholder="Referencia / actividad" aria-label={`Actividad ${label.toLowerCase()}`} />
  </div>;
}

export default function JournalMarksEditor({ row, onClose, onSaved }) {
  const [segments, setSegments] = useState(() => editorSegments(row));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setSegments(editorSegments(row)); }, [row]);

  function updateSegment(index, side, value) {
    setSegments((current) => current.map((segment, position) => (
      position === index ? { ...segment, [side]: value } : segment
    )));
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      const normalizedSegments = segments.map((segment) => ({
        entrada: { ...segment.entrada, hora: normalizeTime24(segment.entrada.hora) },
        salida: { ...segment.salida, hora: normalizeTime24(segment.salida.hora) },
      }));
      if (normalizedSegments.some((segment) => segment.entrada.hora === null || segment.salida.hora === null)) {
        throw new Error('Ingresá las horas en formato 24 horas HH:MM (por ejemplo, 09:30 o 18:00)');
      }
      const payloadSegments = normalizedSegments.map((segment) => ({
        entrada: segment.entrada.hora ? segment.entrada : null,
        salida: segment.salida.hora ? segment.salida : null,
      })).filter((segment) => segment.entrada || segment.salida);
      await rrhhCorePost('/validacion/jornales/marcas', {
        turno_id: row.turno_id,
        persona_id: row.persona_id,
        fecha: row.fecha,
        marca_ids_originales: originalMarkIds(row),
        tramos: payloadSegments,
        observacion: note,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || 'No se pudieron guardar las marcas');
    } finally {
      setSaving(false);
    }
  }

  return <div className="rrhh-journal-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section className="rrhh-journal-modal" role="dialog" aria-modal="true" aria-labelledby="journal-editor-title">
      <header className="rrhh-journal-modal-head">
        <div>
          <span className="rrhh-native-eyebrow">Modificar marcas del turno</span>
          <h2 id="journal-editor-title">{row.persona}</h2>
          <p>{formatDisplayDate(row.fecha)} · Plan {plannedSchedule(row) || 'Sin horario'}</p>
        </div>
        <button className="btn btn-ghost rrhh-journal-close" type="button" onClick={onClose} aria-label="Cerrar editor">×</button>
      </header>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="rrhh-journal-segments">
        {segments.map((segment, index) => <article className="rrhh-journal-segment-editor" key={`${index}-${segment.entrada.id || ''}-${segment.salida.id || ''}`}>
          <header><strong>Tramo {String(index + 1).padStart(2, '0')}</strong><button className="btn btn-ghost btn-sm" type="button" onClick={() => setSegments((current) => current.filter((_, position) => position !== index))}>Quitar tramo</button></header>
          <MarkFields label="Entrada" value={segment.entrada} onChange={(value) => updateSegment(index, 'entrada', value)} />
          <MarkFields label="Salida" value={segment.salida} onChange={(value) => updateSegment(index, 'salida', value)} />
        </article>)}
        <button className="btn btn-secondary rrhh-journal-add-segment" type="button" onClick={() => setSegments((current) => [...current, blankSegment(row.fecha)])}>+ Agregar tramo</button>
      </div>
      <label className="rrhh-journal-note">Observación del cambio<input className="form-input" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Motivo o aclaración opcional" /></label>
      <footer className="rrhh-journal-modal-actions">
        <button className="btn btn-secondary" type="button" onClick={onClose}>Cancelar</button>
        <button className="btn btn-primary" type="button" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar marcas'}</button>
      </footer>
    </section>
  </div>;
}
